import {
  BarChart3,
  Building2,
  CalendarDays,
  ClipboardList,
  Clock,
  FileSpreadsheet,
  FileText,
  HeartPulse,
  Landmark,
  PieChart,
  ShieldCheck,
  Sparkles,
  Table2,
  TrendingUp,
  Users,
} from "lucide-react";

export const REPORTS_CATEGORY_ORDER = [
  "Employee Reports",
  "Attendance Reports",
  "Leave Reports",
  "Payroll Reports",
  "Statutory Reports",
  "Banking Reports",
  "Government Reports",
  "Compliance Reports",
  "Analytics Reports",
  "Custom Reports",
];

export const FAMILY_TO_CATEGORY = {
  epf: "Statutory Reports",
  esi: "Statutory Reports",
  pt: "Statutory Reports",
  lwf: "Statutory Reports",
  tds: "Statutory Reports",
  deductions: "Compliance Reports",
  exceptions: "Compliance Reports",
};

const FAMILY_ICONS = {
  epf: ShieldCheck,
  esi: HeartPulse,
  pt: FileText,
  lwf: Users,
  tds: FileText,
  deductions: FileSpreadsheet,
  exceptions: ShieldCheck,
};

export const CATEGORY_ICONS = {
  "Employee Reports": Users,
  "Payroll Reports": FileSpreadsheet,
  "Statutory Reports": ShieldCheck,
  "Banking Reports": Landmark,
  "Attendance Reports": CalendarDays,
  "Leave Reports": CalendarDays,
  "Government Reports": Building2,
  "Compliance Reports": ShieldCheck,
  "Analytics Reports": BarChart3,
  "Custom Reports": Sparkles,
};

export const CATEGORY_COLORS = {
  "Employee Reports":   { bg: "rgba(59, 130, 246, 0.10)", icon: "#3b82f6", border: "rgba(59, 130, 246, 0.25)" },
  "Attendance Reports": { bg: "rgba(20, 184, 166, 0.10)", icon: "#14b8a6", border: "rgba(20, 184, 166, 0.25)" },
  "Leave Reports":      { bg: "rgba(168, 85, 247, 0.10)", icon: "#a855f7", border: "rgba(168, 85, 247, 0.25)" },
  "Payroll Reports":    { bg: "rgba(34, 197, 94, 0.10)",  icon: "#22c55e", border: "rgba(34, 197, 94, 0.25)"  },
  "Statutory Reports":  { bg: "rgba(245, 158, 11, 0.10)", icon: "#f59e0b", border: "rgba(245, 158, 11, 0.25)" },
  "Banking Reports":    { bg: "rgba(99, 102, 241, 0.10)", icon: "#6366f1", border: "rgba(99, 102, 241, 0.25)" },
  "Government Reports": { bg: "rgba(239, 68, 68, 0.10)",  icon: "#ef4444", border: "rgba(239, 68, 68, 0.25)"  },
  "Compliance Reports": { bg: "rgba(236, 72, 153, 0.10)", icon: "#ec4899", border: "rgba(236, 72, 153, 0.25)" },
  "Analytics Reports":  { bg: "rgba(14, 165, 233, 0.10)", icon: "#0ea5e9", border: "rgba(14, 165, 233, 0.25)" },
  "Custom Reports":     { bg: "rgba(251, 146, 60, 0.10)", icon: "#fb923c", border: "rgba(251, 146, 60, 0.25)" },
};

/** Cross-workspace links shown under Government Reports. */
export const GOVERNMENT_REPORT_LINKS = [
  {
    key: "gov_tds_compliance",
    name: "TDS Compliance Center",
    description: "TDS returns, challans, Form 16, and regulatory filings.",
    href: "/settings/tds/reports",
    icon: FileText,
    category: "Government Reports",
  },
  {
    key: "gov_form16",
    name: "Form 16 (Statutory)",
    description: "Form 16 generation status from the statutory reports suite.",
    href: "/reports/statutory?report=tds_form16_report",
    icon: FileText,
    category: "Government Reports",
  },
];

/** Banking & leave routes outside the payroll hub. */
export const EXTERNAL_REPORT_NAV = [
  {
    key: "bank_disbursement",
    name: "Bank Disbursement Register",
    description: "Batch-wise bank transfer register and payout status.",
    href: "/bank/reports",
    icon: Landmark,
    category: "Banking Reports",
  },
  {
    key: "leave_balance",
    name: "Leave Balance Report",
    description: "Current leave balance of employees.",
    href: "/reports/leave?report=leave_balance",
    icon: PieChart,
    category: "Leave Reports",
  },
  {
    key: "leave_register",
    name: "Leave Register",
    description: "All leave transactions for the selected period.",
    href: "/reports/leave?report=leave_register",
    icon: ClipboardList,
    category: "Leave Reports",
  },
  {
    key: "leave_utilization",
    name: "Leave Utilization Report",
    description: "Leave availed vs remaining by department.",
    href: "/reports/leave?report=leave_utilization",
    icon: TrendingUp,
    category: "Leave Reports",
  },
  {
    key: "leave_pending",
    name: "Pending Leave Requests",
    description: "Leave requests awaiting approval.",
    href: "/reports/leave?report=leave_pending",
    icon: Clock,
    category: "Leave Reports",
  },
];

export const LEAVE_REPORT_NAV_KEYS = [
  "leave_balance",
  "leave_register",
  "leave_utilization",
  "leave_pending",
];

export function catalogItemToNavItem(item) {
  if (!item?.report_key) return null;
  const category = FAMILY_TO_CATEGORY[item.family] || "Statutory Reports";
  const Icon = FAMILY_ICONS[item.family] || ShieldCheck;
  if (item.report_key === "esi_monthly") {
    return {
      key: item.report_key,
      name: item.name,
      description: item.description,
      category,
      icon: Icon,
      href: "/reports/esi",
    };
  }
  return {
    key: item.report_key,
    name: item.name,
    description: item.description,
    category,
    icon: Icon,
    href: `/reports/statutory?report=${encodeURIComponent(item.report_key)}`,
  };
}

export function payrollReportToNavItem(report) {
  if (!report?.key) return null;
  if (report.isLink && report.href) {
    return {
      key: report.key,
      name: report.name,
      description: report.description,
      category: report.category,
      icon: report.icon,
      href: report.href,
    };
  }
  return {
    key: report.key,
    name: report.name,
    description: report.description,
    category: report.category,
    icon: report.icon,
    href: `/reports/payroll?report=${encodeURIComponent(report.key)}`,
  };
}

export function buildReportsByCategory({
  payrollReports = [],
  catalog = [],
  extraNav = [],
  search = "",
} = {}) {
  const q = search.trim().toLowerCase();
  const match = (item) =>
    !q ||
    item.name.toLowerCase().includes(q) ||
    (item.description || "").toLowerCase().includes(q);

  const groups = {};
  const seen = new Set();

  const add = (item) => {
    if (!item || !match(item)) return;
    const dedupeKey = item.href || item.key;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    const cat = item.category || "Payroll Reports";
    (groups[cat] = groups[cat] || []).push(item);
  };

  const catalogKeys = new Set(catalog.map((c) => c.report_key));
  const skipPayrollKeys = new Set();
  if (catalogKeys.has("epf_monthly")) skipPayrollKeys.add("pf");
  if (catalogKeys.has("esi_monthly")) skipPayrollKeys.add("esi");

  payrollReports.forEach((r) => {
    if (skipPayrollKeys.has(r.key)) return;
    add(payrollReportToNavItem(r));
  });
  catalog.forEach((c) => add(catalogItemToNavItem(c)));
  [...GOVERNMENT_REPORT_LINKS, ...EXTERNAL_REPORT_NAV, ...extraNav].forEach(add);

  for (const cat of Object.keys(groups)) {
    groups[cat].sort((a, b) => a.name.localeCompare(b.name));
  }
  return groups;
}

export function resolveActiveNavItem({ pathname, search, payrollReports = [], catalog = [] }) {
  const params = new URLSearchParams(search);
  const reportParam = params.get("report");

  if (pathname === "/reports/esi") {
    return { key: "esi_monthly", href: "/reports/esi", name: "ESI Report" };
  }
  if (pathname === "/reports/statutory" && reportParam) {
    const cat = catalog.find((c) => c.report_key === reportParam);
    if (cat) return catalogItemToNavItem(cat);
    return { key: reportParam, href: `${pathname}?report=${reportParam}`, name: reportParam };
  }
  if (pathname === "/reports/payroll/employee-salary-register") {
    return payrollReportToNavItem(
      payrollReports.find((r) => r.key === "employee_salary_register") || {
        key: "employee_salary_register",
        name: "Employee Salary Register",
        href: "/reports/payroll/employee-salary-register",
        category: "Payroll Reports",
        icon: Table2,
      }
    );
  }
  if (pathname === "/reports/payroll" && reportParam) {
    const pr = payrollReports.find((r) => r.key === reportParam);
    if (pr) return payrollReportToNavItem(pr);
    return null;
  }
  if (pathname === "/bank/reports") {
    return EXTERNAL_REPORT_NAV.find((n) => n.key === "bank_disbursement");
  }
  if (pathname === "/reports/leave") {
    const leaveKey = reportParam || "leave_balance";
    return (
      EXTERNAL_REPORT_NAV.find((n) => n.key === leaveKey) ||
      EXTERNAL_REPORT_NAV.find((n) => n.key === "leave_balance")
    );
  }
  if (pathname.startsWith("/settings/tds/reports")) {
    return GOVERNMENT_REPORT_LINKS.find((n) => n.key === "gov_tds_compliance");
  }
  // Landing on /reports/payroll with no ?report= shows the getting-started guide.
  if (pathname === "/reports/payroll") {
    return null;
  }
  return null;
}

export function isReportsRoute(pathname = "") {
  return (
    pathname.startsWith("/reports/") ||
    pathname === "/bank/reports" ||
    pathname.startsWith("/settings/tds/reports")
  );
}
